import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Switch,
  TextField,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  alpha,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Security,
  Psychology,
  Storage,
  Update,
  ExpandMore,
  Save,
  RestartAlt,
  Palette,
  Language,
  LocationOn,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';

interface SystemSettings {
  // AI Model Settings
  aiModel: {
    confidenceThreshold: number;
    enableAdvancedAnalysis: boolean;
    modelVersion: string;
    processingTimeout: number;
  };
  
  // Security Settings
  security: {
    enableTwoFactor: boolean;
    sessionTimeout: number;
    maxLoginAttempts: number;
    requireStrongPasswords: boolean;
    enableAuditLog: boolean;
  };
  
  // System Settings
  system: {
    enableMaintenance: boolean;
    maxFileSize: number;
    allowedFileTypes: string[];
    enableBackups: boolean;
    backupFrequency: string;
    enableNotifications: boolean;
  };
  
  // UI Settings
  ui: {
    defaultTheme: string;
    enableAnimations: boolean;
    language: string;
    timezone: string;
  };
}

const defaultSettings: SystemSettings = {
  aiModel: {
    confidenceThreshold: 0.75,
    enableAdvancedAnalysis: true,
    modelVersion: 'v2.1.0',
    processingTimeout: 30,
  },
  security: {
    enableTwoFactor: false,
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    requireStrongPasswords: true,
    enableAuditLog: true,
  },
  system: {
    enableMaintenance: false,
    maxFileSize: 10,
    allowedFileTypes: ['jpg', 'jpeg', 'png'],
    enableBackups: true,
    backupFrequency: 'daily',
    enableNotifications: true,
  },
  ui: {
    defaultTheme: 'light',
    enableAnimations: true,
    language: 'en',
    timezone: 'UTC',
  },
};

const SystemSettingsPage: React.FC = () => {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch system settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: () => apiService.getSystemSettings(),
    enabled: isAdmin,
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (newSettings: any) => apiService.updateSystemSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      setHasChanges(false);
    },
  });

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Access denied. This page is only available to administrators.
      </Alert>
    );
  }

  const handleSettingChange = (category: string, key: string, value: any) => {
    // In a real implementation, you'd update local state and track changes
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    if (settings) {
      updateSettingsMutation.mutate(settings);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      updateSettingsMutation.mutate(defaultSettings);
      setHasChanges(true);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Box>
        {/* Header Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  ⚙️ System Settings
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Configure AI models, security, and system preferences
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2}>
                <Tooltip title="Reset to Defaults">
                  <IconButton
                    onClick={handleResetSettings}
                    sx={{
                      background: alpha(theme.palette.warning.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.warning.main, 0.2),
                      },
                    }}
                  >
                    <RestartAlt />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="contained"
                  startIcon={updateSettingsMutation.isPending ? <Update className="animate-spin" /> : <Save />}
                  disabled={!hasChanges || updateSettingsMutation.isPending}
                  onClick={handleSaveSettings}
                  sx={{
                    background: hasChanges
                      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                      : undefined,
                    '&:hover': hasChanges ? {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    } : undefined,
                  }}
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            </Box>

            {hasChanges && (
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  background: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                You have unsaved changes. Don't forget to save your settings.
              </Alert>
            )}
          </Box>
        </Fade>

        {/* Settings Sections */}
        <Grid container spacing={3}>
          {/* AI Model Settings */}
          <Grid item xs={12}>
            <Accordion
              defaultExpanded
              elevation={0}
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                '&::before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <Psychology />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      🤖 AI Model Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure AI analysis parameters and model settings
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>
              
              <AccordionDetails sx={{ p: 4 }}>
                {settingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Confidence Threshold
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Minimum confidence level for AI predictions (0.0 - 1.0)
                    </Typography>
                    <Slider
                      value={settings?.aiModel?.confidenceThreshold || 0.8}
                      onChange={(_, value) => handleSettingChange('aiModel', 'confidenceThreshold', value)}
                      min={0}
                      max={1}
                      step={0.05}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 0.5, label: '50%' },
                        { value: 1, label: '100%' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
                      sx={{
                        '& .MuiSlider-thumb': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        },
                        '& .MuiSlider-track': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Processing Timeout
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Maximum time for AI analysis (seconds)
                    </Typography>
                    <TextField
                      type="number"
                      value={settings?.aiModel?.processingTimeout || 60}
                      onChange={(e) => handleSettingChange('aiModel', 'processingTimeout', parseInt(e.target.value))}
                      inputProps={{ min: 10, max: 300 }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Model Version</InputLabel>
                      <Select
                        value={settings?.aiModel?.modelVersion || 'v2.1.0'}
                        label="Model Version"
                        onChange={(e) => handleSettingChange('aiModel', 'modelVersion', e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="v2.1.0">v2.1.0 (Latest)</MenuItem>
                        <MenuItem value="v2.0.0">v2.0.0 (Stable)</MenuItem>
                        <MenuItem value="v1.9.0">v1.9.0 (Legacy)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.aiModel?.enableAdvancedAnalysis || false}
                          onChange={(e) => handleSettingChange('aiModel', 'enableAdvancedAnalysis', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Advanced Analysis
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Enable ABCDE criteria analysis
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Security Settings */}
          <Grid item xs={12}>
            <Accordion
              elevation={0}
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                '&::before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      🔒 Security & Authentication
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure security policies and authentication settings
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.security?.enableTwoFactor || false}
                          onChange={(e) => handleSettingChange('security', 'enableTwoFactor', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.error.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.error.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Two-Factor Authentication
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Require 2FA for all admin accounts
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.security?.requireStrongPasswords || false}
                          onChange={(e) => handleSettingChange('security', 'requireStrongPasswords', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.error.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.error.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Strong Password Policy
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Enforce complex password requirements
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Session Timeout (hours)
                    </Typography>
                    <TextField
                      type="number"
                      value={settings?.security?.sessionTimeout || 24}
                      onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 168 }}
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Max Login Attempts
                    </Typography>
                    <TextField
                      type="number"
                      value={settings?.security?.maxLoginAttempts || 5}
                      onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                      inputProps={{ min: 3, max: 10 }}
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.security?.enableAuditLog || false}
                          onChange={(e) => handleSettingChange('security', 'enableAuditLog', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.error.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.error.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Enable Audit Logging
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Log all user actions and system events for security monitoring
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* System Settings */}
          <Grid item xs={12}>
            <Accordion
              elevation={0}
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                '&::before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                    <Storage />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      💾 System Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure file handling, backups, and system maintenance
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Maximum File Size (MB)
                    </Typography>
                    <TextField
                      type="number"
                      value={settings?.system?.maxFileSize || 10}
                      onChange={(e) => handleSettingChange('system', 'maxFileSize', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 100 }}
                      fullWidth
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select
                        value={settings?.system?.backupFrequency || 'daily'}
                        label="Backup Frequency"
                        onChange={(e) => handleSettingChange('system', 'backupFrequency', e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="hourly">Hourly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.system?.enableBackups || false}
                          onChange={(e) => handleSettingChange('system', 'enableBackups', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.success.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.success.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Automatic Backups
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Enable scheduled system backups
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.system?.enableMaintenance || false}
                          onChange={(e) => handleSettingChange('system', 'enableMaintenance', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.warning.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.warning.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Maintenance Mode
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Temporarily disable user access
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.system?.enableNotifications || false}
                          onChange={(e) => handleSettingChange('system', 'enableNotifications', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.success.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.success.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            System Notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Enable system-wide notifications
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Allowed File Types
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Supported image formats for analysis
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {(settings?.system?.allowedFileTypes || ['jpg', 'jpeg', 'png']).map((type: string) => (
                        <Chip
                          key={type}
                          label={type.toUpperCase()}
                          color="primary"
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderWidth: 2,
                          }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* UI Settings */}
          <Grid item xs={12}>
            <Accordion
              elevation={0}
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                '&::before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                    <Palette />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      🎨 User Interface Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure theme, language, and display preferences
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Default Theme</InputLabel>
                      <Select
                        value={settings?.ui?.defaultTheme || 'light'}
                        label="Default Theme"
                        onChange={(e) => handleSettingChange('ui', 'defaultTheme', e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="auto">Auto (System)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={settings?.ui?.language || 'en'}
                        label="Language"
                        onChange={(e) => handleSettingChange('ui', 'language', e.target.value)}
                        sx={{ borderRadius: 2 }}
                        startAdornment={<Language sx={{ mr: 1, color: 'text.secondary' }} />}
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="es">Español</MenuItem>
                        <MenuItem value="fr">Français</MenuItem>
                        <MenuItem value="de">Deutsch</MenuItem>
                        <MenuItem value="pt">Português</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={settings?.ui?.timezone || 'UTC'}
                        label="Timezone"
                        onChange={(e) => handleSettingChange('ui', 'timezone', e.target.value)}
                        sx={{ borderRadius: 2 }}
                        startAdornment={<LocationOn sx={{ mr: 1, color: 'text.secondary' }} />}
                      >
                        <MenuItem value="UTC">UTC (Coordinated Universal Time)</MenuItem>
                        <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                        <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                        <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                        <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                        <MenuItem value="Europe/London">London (GMT)</MenuItem>
                        <MenuItem value="Europe/Paris">Paris (CET)</MenuItem>
                        <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                        <MenuItem value="Australia/Sydney">Sydney (AEDT)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings?.ui?.enableAnimations || false}
                          onChange={(e) => handleSettingChange('ui', 'enableAnimations', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.info.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.info.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Enable Animations
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Show smooth transitions and animations
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Box>
    </motion.div>
  );
};

export default SystemSettingsPage;
